import React, { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

/**
 * Lightweight controlled autocomplete. Free-text always allowed (the input value
 * is passed straight through onChange). Suggestions filter against `options` by
 * case-insensitive substring match on the typed value.
 */
export default function Autocomplete({
  value,
  onChange,
  options,
  placeholder,
  testId,
  className = "",
  maxResults = 8,
  inputClassName = "ta-svc-input",
}) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const q = (value || "").trim().toLowerCase();
  const matches = options
    .filter((o) => !q || o.toLowerCase().includes(q))
    // exact-match-first, then prefix-match, then substring
    .sort((a, b) => {
      const al = a.toLowerCase(), bl = b.toLowerCase();
      const ap = al.startsWith(q) ? 0 : 1;
      const bp = bl.startsWith(q) ? 0 : 1;
      if (ap !== bp) return ap - bp;
      return al.localeCompare(bl);
    })
    .slice(0, maxResults);

  const pick = (val) => {
    onChange(val);
    setOpen(false);
    setHighlight(-1);
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(matches.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter" && highlight >= 0 && matches[highlight]) {
      e.preventDefault();
      pick(matches[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <input
        data-testid={testId}
        type="text"
        autoComplete="off"
        className={`${inputClassName} pr-9`}
        value={value || ""}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setHighlight(-1);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
      />
      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500 pointer-events-none" />
      {open && matches.length > 0 && (
        <ul
          data-testid={`${testId}-options`}
          className="absolute z-30 top-full left-0 right-0 mt-1 max-h-64 overflow-y-auto bg-zinc-950 border border-zinc-800 shadow-xl"
        >
          {matches.map((opt, i) => (
            <li
              key={opt}
              data-testid={`${testId}-option-${opt}`}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(opt);
              }}
              onMouseEnter={() => setHighlight(i)}
              className={`px-3 py-2 text-sm cursor-pointer font-mono uppercase tracking-wider ${
                i === highlight
                  ? "bg-brand-red/15 text-brand-red"
                  : "text-zinc-200 hover:bg-zinc-900"
              }`}
            >
              {opt}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
